// Admin: создание broadcast-уведомлений. Phase 1 MVP, без preview карточек.

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Megaphone } from 'lucide-react';
import { adminNotificationsAPI, AdminCreateNotificationPayload } from '../services/notificationsAPI';

const ALL_CONFIRMATION = 'УВЕРЕН';

const SEVERITIES: Array<AdminCreateNotificationPayload['severity']> = ['info', 'success', 'warning', 'error'];
const AUDIENCES: Array<AdminCreateNotificationPayload['audience_type']> = ['user', 'all', 'admin'];
const CHANNELS = ['inbox', 'email', 'telegram', 'webpush'];

const NotificationsAdminPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [severity, setSeverity] = useState<AdminCreateNotificationPayload['severity']>('info');
  const [audienceType, setAudienceType] = useState<AdminCreateNotificationPayload['audience_type']>('user');
  const [audienceUserID, setAudienceUserID] = useState('');
  const [channels, setChannels] = useState<string[]>(['inbox']);
  const [confirmAll, setConfirmAll] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const titleLen = title.length;
  const bodyLen = body.length;

  const onChannelToggle = (c: string) => {
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const buildPayload = (): AdminCreateNotificationPayload => {
    const filter: Record<string, unknown> = {};
    if (audienceType === 'user' && audienceUserID) {
      filter.user_id = parseInt(audienceUserID, 10);
    }
    return {
      title,
      body,
      link: link || undefined,
      severity,
      audience_type: audienceType,
      audience_filter: Object.keys(filter).length ? filter : undefined,
      channels,
    };
  };

  const handlePreview = async () => {
    try {
      const filter = audienceType === 'user' && audienceUserID
        ? { user_id: parseInt(audienceUserID, 10) }
        : undefined;
      const c = await adminNotificationsAPI.previewAudience(audienceType, filter);
      setPreviewCount(c);
    } catch (e) {
      toast.error('Не удалось оценить аудиторию');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (titleLen === 0 || titleLen > 80) {
      toast.error('Заголовок 1–80 символов');
      return;
    }
    if (bodyLen === 0 || bodyLen > 280) {
      toast.error('Текст 1–280 символов');
      return;
    }
    if (audienceType === 'all' && confirmAll !== ALL_CONFIRMATION) {
      toast.error(`Подтвердите рассылку: введите ${ALL_CONFIRMATION}`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminNotificationsAPI.create(buildPayload());
      toast.success(`Создано: id=${res.id}. Будет отправлено в ближайший тик.`);
      setTitle('');
      setBody('');
      setLink('');
      setConfirmAll('');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Не удалось отправить');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <Megaphone size={20} />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Уведомления — рассылка</h1>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Заголовок ({titleLen}/80)</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={80}
            required
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Текст ({bodyLen}/280)</span>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={280}
            rows={3}
            required
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Ссылка (опционально)</span>
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="/dashboard или https://…"
            style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>Тип</span>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as any)}
              style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>Аудитория</span>
            <select
              value={audienceType}
              onChange={(e) => { setAudienceType(e.target.value as any); setPreviewCount(null); }}
              style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            >
              {AUDIENCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>

        {audienceType === 'user' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#374151' }}>User ID</span>
            <input
              type="number"
              value={audienceUserID}
              onChange={(e) => setAudienceUserID(e.target.value)}
              required
              style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            />
          </label>
        )}

        <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
          <legend style={{ fontSize: 13, color: '#374151', padding: '0 6px' }}>Каналы</legend>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {CHANNELS.map((c) => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <input type="checkbox" checked={channels.includes(c)} onChange={() => onChannelToggle(c)} />
                {c}
              </label>
            ))}
          </div>
        </fieldset>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={handlePreview}
            style={{ padding: '8px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}
          >
            Оценить аудиторию
          </button>
          {previewCount !== null && <span style={{ fontSize: 14, color: '#374151' }}>≈ {previewCount} получателей</span>}
        </div>

        {audienceType === 'all' && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, color: '#dc2626' }}>
              Подтвердите массовую рассылку. Введите <b>{ALL_CONFIRMATION}</b>:
            </span>
            <input
              type="text"
              value={confirmAll}
              onChange={(e) => setConfirmAll(e.target.value)}
              style={{ padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
            />
          </label>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{
            padding: '10px 16px',
            background: '#2563eb',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.6 : 1,
          }}
        >
          {submitting ? 'Отправка…' : 'Создать уведомление'}
        </button>
      </form>
    </div>
  );
};

export default NotificationsAdminPage;
